// manufacturing/StockTransfer.jsx - Fixed with proper item loading in edit modal
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { OverlayTrigger, Tooltip, Modal, Button } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import Select from 'react-select';
import {
    Plus, Search, RotateCcw, Eye, Edit, Trash2, Printer, ChevronUp,
    Shuffle, Calendar, MapPin, Package, DollarSign, CheckCircle, AlertCircle, XCircle,
} from 'feather-icons-react/build/IconComponents';
import { manufacturingActions } from "../../core/action/manufacturingAction";
import { productAPI } from "../../core/action/api";
import ImageWithBasePath from '../../core/img/imagewithbasebath';

const MySwal = withReactContent(Swal);

const formatDate = (date) => date
    ? new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : 'N/A';
const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(amount || 0);

const StockTransfer = () => {
    const dispatch = useDispatch();
    const reduxData = useSelector((state) => state.toggle_header);

    const [transfers, setTransfers] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedTransfer, setSelectedTransfer] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const emptyForm = {
        transfer_date: new Date().toISOString().split('T')[0],
        from_location: '',
        to_location: '',
        transfer_type: 'finished_good',
        notes: '',
        items: [],
    };
    const [formData, setFormData] = useState(emptyForm);
    const [errors, setErrors] = useState({});

    const [filters, setFilters] = useState({
        search: '', status: '', transfer_type: '', from_location: '', to_location: '', from_date: '', to_date: ''
    });
    const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });

    const locationOptions = manufacturingActions.getLocationOptions();
    const transferTypeOptions = [
        { value: 'raw_material', label: 'Raw Material Transfer' },
        { value: 'finished_good', label: 'Finished Good Transfer' },
    ];
    const statusOptions = [
        { value: 'pending', label: 'Pending' },
        { value: 'completed', label: 'Completed' },
        { value: 'cancelled', label: 'Cancelled' },
    ];

    const canCreate = manufacturingActions.hasManufacturingPermission('manage-stock-transfer');
    const canView = manufacturingActions.hasManufacturingPermission('view-stock-transfer');
    const canEdit = manufacturingActions.hasManufacturingPermission('edit-stock-transfer');

    // Load transfers
    const loadTransfers = useCallback(async (page = 1, currentFilters = filters) => {
        if (!canView) { toast.error('No permission to view transfers'); return; }
        setLoading(true);
        try {
            const result = await manufacturingActions.getTransfers({ page, limit: pagination.limit, ...currentFilters });
            if (result.success) {
                setTransfers(result.data || []);
                setPagination(prev => ({ ...prev, page, total: result.total || 0, totalPages: result.totalPages || 1 }));
            } else {
                toast.error(result.message || 'Failed to load transfers');
            }
        } catch {
            toast.error('Failed to load transfers');
        } finally {
            setLoading(false);
        }
    }, [pagination.limit, canView]);

    const normalizeProducts = (response) => {
        if (!response) return [];
        if (Array.isArray(response.data)) return response.data;
        if (Array.isArray(response.data?.products)) return response.data.products;
        if (Array.isArray(response.products)) return response.products;
        return [];
    };

    const loadProducts = async () => {
        try {
            const response = await productAPI.list({ limit: 1000, status: 'active' });
            const productsArray = normalizeProducts(response);
            // Ensure products have proper quantity field
            const formattedProducts = productsArray.map(p => ({
                ...p,
                id: p.id,
                product_name: p.product_name,
                sku: p.sku,
                unit_name: p.unit_name || 'PCS',
                cost_price: p.cost_price || p.selling_price || 0,
                quantity: p.quantity || p.current_stock || 0
            }));
            setProducts(formattedProducts);
        } catch { 
            setProducts([]); 
        }
    };

    useEffect(() => { 
        loadTransfers(1, filters); 
        loadProducts(); 
    }, []);

    // Filter change
    const handleFilterChange = (key, value) => {
        const updated = { ...filters, [key]: value };
        setFilters(updated);
        loadTransfers(1, updated);
    };

    const clearFilters = () => {
        const empty = { search: '', status: '', transfer_type: '', from_location: '', to_location: '', from_date: '', to_date: '' };
        setFilters(empty);
        loadTransfers(1, empty);
    };

    // Item management
    const addItem = () => {
        setFormData(prev => ({
            ...prev,
            items: [...prev.items, { 
                id: Date.now(), 
                product_id: '', 
                product_name: '', 
                sku: '', 
                quantity: 0, 
                unit: 'PCS', 
                unit_cost: 0, 
                total_cost: 0, 
                remarks: '' 
            }]
        }));
    };

    const removeItem = (idx) => {
        setFormData(prev => ({ 
            ...prev, 
            items: prev.items.filter((_, i) => i !== idx) 
        }));
    };

    const handleProductSelect = (idx, sel) => {
        const product = products.find(p => p.id === sel?.value);
        if (!product) return;
        
        setFormData(prev => {
            const items = [...prev.items];
            const qty = items[idx].quantity || 0;
            items[idx] = {
                ...items[idx],
                product_id: product.id,
                product_name: product.product_name,
                sku: product.sku,
                unit: product.unit_name || 'PCS',
                unit_cost: parseFloat(product.cost_price || 0),
                total_cost: parseFloat(product.cost_price || 0) * qty,
            };
            return { ...prev, items };
        });
        
        // Clear product error if exists
        if (errors[`product_${idx}`]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[`product_${idx}`];
                return newErrors;
            });
        }
    };

    const handleQtyChange = (idx, qty) => {
        const quantity = parseFloat(qty) || 0;
        
        setFormData(prev => {
            const items = [...prev.items];
            const newTotalCost = items[idx].unit_cost * quantity;
            items[idx] = { 
                ...items[idx], 
                quantity: quantity, 
                total_cost: newTotalCost 
            };
            return { ...prev, items };
        });
        
        // Check stock
        const product = products.find(p => p.id === formData.items[idx]?.product_id);
        if (product && quantity > (parseFloat(product.quantity) || 0)) {
            setErrors(prev => ({ 
                ...prev, 
                [`stock_${idx}`]: `Insufficient stock. Available: ${product.quantity}` 
            }));
        } else {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[`stock_${idx}`];
                return newErrors;
            });
        }
        
        // Clear quantity error
        if (errors[`qty_${idx}`]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[`qty_${idx}`];
                return newErrors;
            });
        }
    };

    const handleCostChange = (idx, cost) => {
        const unitCost = parseFloat(cost) || 0;
        setFormData(prev => {
            const items = [...prev.items];
            items[idx] = { 
                ...items[idx], 
                unit_cost: unitCost, 
                total_cost: unitCost * (items[idx].quantity || 0) 
            };
            return { ...prev, items };
        });
    };

    // Validate form
    const validateForm = (data) => {
        const errs = {};
        if (!data.from_location) errs.from_location = 'Source location required';
        if (!data.to_location) errs.to_location = 'Destination location required';
        if (data.from_location === data.to_location && data.from_location)
            errs.to_location = 'Source and destination cannot be the same';
        if (!data.items.length) errs.items = 'At least one product required';
        
        let hasValidItem = false;
        data.items.forEach((item, i) => {
            if (item.quantity > 0 && !item.product_id) {
                errs[`product_${i}`] = 'Select a product';
            }
            if (item.product_id && (!item.quantity || item.quantity <= 0)) {
                errs[`qty_${i}`] = 'Quantity must be > 0';
            }
            if (item.product_id && item.quantity > 0) {
                hasValidItem = true;
            }
        });
        
        if (!hasValidItem && data.items.length > 0) {
            errs.items = 'At least one valid item with product and quantity > 0 required';
        }
        
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    // Create transfer
    const handleSubmit = async () => {
        if (!validateForm(formData)) { 
            toast.error('Please fix form errors'); 
            return; 
        }
        setSubmitting(true);
        try {
            const payload = {
                transfer_date: formData.transfer_date,
                from_location: formData.from_location,
                to_location: formData.to_location,
                transfer_type: formData.transfer_type,
                notes: formData.notes || '',
                items: formData.items
                    .filter(i => i.product_id && i.quantity > 0)
                    .map(i => ({ 
                        product_id: i.product_id, 
                        quantity: i.quantity, 
                        unit: i.unit, 
                        unit_cost: i.unit_cost, 
                        remarks: i.remarks 
                    })),
            };
            const result = await manufacturingActions.createTransfer(payload);
            if (result.success) {
                toast.success(result.message || 'Transfer created successfully');
                setFormData(emptyForm); 
                setErrors({});
                setShowCreateModal(false);
                loadTransfers(1, filters);
            } else {
                toast.error(result.message || 'Failed to create transfer');
            }
        } catch (err) {
            toast.error(err.message || 'Failed to create transfer');
        } finally {
            setSubmitting(false);
        }
    };

    // Update transfer - FIXED
    const handleUpdateTransfer = async () => {
        if (!validateForm(formData)) { 
            toast.error('Please fix form errors'); 
            return; 
        }
        
        // Ensure we have valid items
        const validItems = formData.items.filter(i => i.product_id && i.quantity > 0);
        if (validItems.length === 0) {
            toast.error('At least one valid item is required');
            return;
        }
        
        setSubmitting(true);
        try {
            const payload = {
                transfer_date: formData.transfer_date,
                from_location: formData.from_location,
                to_location: formData.to_location,
                transfer_type: formData.transfer_type,
                notes: formData.notes || '',
                items: validItems.map(i => ({ 
                    product_id: i.product_id, 
                    quantity: parseFloat(i.quantity), 
                    unit: i.unit || 'PCS', 
                    unit_cost: parseFloat(i.unit_cost) || 0, 
                    remarks: i.remarks || '' 
                })),
            };
            
            const result = await manufacturingActions.updateTransfer(selectedTransfer.id, payload);
            if (result.success) {
                toast.success('Transfer updated successfully');
                setFormData(emptyForm);
                setErrors({});
                setShowEditModal(false);
                setSelectedTransfer(null);
                loadTransfers(pagination.page, filters);
            } else {
                toast.error(result.message || 'Failed to update transfer');
            }
        } catch (err) {
            console.error('Update error:', err);
            toast.error(err.message || 'Failed to update transfer');
        } finally {
            setSubmitting(false);
        }
    };

    // View transfer
    const handleViewTransfer = async (transfer) => {
        try {
            const result = await manufacturingActions.getTransfer(transfer.id);
            if (result.success) { 
                setSelectedTransfer(result.data); 
                setShowViewModal(true); 
            } else {
                // Fallback: show what we have from list
                setSelectedTransfer({ ...transfer, items: [] });
                setShowViewModal(true);
                toast.warn('Showing summary only (detailed items failed to load)');
            }
        } catch { 
            toast.error('Failed to load transfer details'); 
        }
    };

// Edit transfer - FIXED to properly load items in the table
const handleEditTransfer = async (transfer) => {
    if (!canEdit) { 
        toast.error('No permission to edit transfers'); 
        return; 
    }
    
    console.log('Opening Edit for transfer:', transfer);
    
    try {
        // First ensure products are loaded
        if (products.length === 0) {
            await loadProducts();
        }
        
        const result = await manufacturingActions.getTransfer(transfer.id);
        console.log('Edit API response:', result);
        
        if (result && result.success && result.data) {
            const d = result.data;
            
            // Debug: Log the items to see what we're getting from API
            console.log('Raw items from API:', d.items);
            
            // Properly map items with all required fields
            // Handle different possible item structures
            let itemsArray = [];
            
            if (d.items && Array.isArray(d.items)) {
                itemsArray = d.items;
            } else if (d.transfer_items && Array.isArray(d.transfer_items)) {
                itemsArray = d.transfer_items;
            } else if (d.stock_transfer_items && Array.isArray(d.stock_transfer_items)) {
                itemsArray = d.stock_transfer_items;
            }
            
            const mappedItems = itemsArray.map((item, index) => ({
                id: item.id || Date.now() + index,
                product_id: item.product_id,
                product_name: item.product_name || item.productName || '',
                sku: item.sku || '',
                quantity: parseFloat(item.quantity) || 0,
                unit: item.unit || 'PCS',
                unit_cost: parseFloat(item.unit_cost || item.unitCost || 0),
                total_cost: parseFloat(item.total_cost || item.totalCost || (parseFloat(item.quantity) * parseFloat(item.unit_cost))) || 0,
                remarks: item.remarks || item.remark || ''
            }));
            
            console.log('Mapped items for edit modal:', mappedItems);
            
            setSelectedTransfer(d);
            setFormData({
                transfer_date: d.transfer_date || new Date().toISOString().split('T')[0],
                from_location: d.from_location || '',
                to_location: d.to_location || '',
                transfer_type: d.transfer_type || 'finished_good',
                notes: d.notes || '',
                items: mappedItems,
            });
            
            // Force a re-render of the items table
            setErrors({});
            setShowEditModal(true);
            
            if (mappedItems.length === 0) {
                toast.warn('No items found in this transfer. You can add items.');
            } else {
                toast.success(`Loaded ${mappedItems.length} item(s) for editing`);
            }
        } else {
            // Fallback when API fails - use list data
            console.warn('Edit API failed, using fallback from list data');
            console.log('Transfer list data:', transfer);
            
            // Try to get items from the transfer object if available
            let existingItems = [];
            if (transfer.items && Array.isArray(transfer.items)) {
                existingItems = transfer.items;
            } else if (transfer.transfer_items && Array.isArray(transfer.transfer_items)) {
                existingItems = transfer.transfer_items;
            }
            
            const mappedItems = existingItems.map((item, index) => ({
                id: item.id || Date.now() + index,
                product_id: item.product_id,
                product_name: item.product_name || '',
                sku: item.sku || '',
                quantity: parseFloat(item.quantity) || 0,
                unit: item.unit || 'PCS',
                unit_cost: parseFloat(item.unit_cost || 0),
                total_cost: parseFloat(item.total_cost || 0),
                remarks: item.remarks || ''
            }));
            
            setSelectedTransfer(transfer);
            setFormData({
                transfer_date: transfer.transfer_date || new Date().toISOString().split('T')[0],
                from_location: transfer.from_location || '',
                to_location: transfer.to_location || '',
                transfer_type: transfer.transfer_type || 'finished_good',
                notes: transfer.notes || '',
                items: mappedItems,
            });
            setShowEditModal(true);
            
            if (mappedItems.length === 0) {
                toast.warn('Unable to load item details. You can edit header info or add new items.');
            } else {
                toast.info(`Loaded ${mappedItems.length} item(s) from list data`);
            }
        }
    } catch (err) { 
        console.error('handleEditTransfer error:', err);
        // Fallback on error
        setSelectedTransfer(transfer);
        setFormData({
            transfer_date: transfer.transfer_date || new Date().toISOString().split('T')[0],
            from_location: transfer.from_location || '',
            to_location: transfer.to_location || '',
            transfer_type: transfer.transfer_type || 'finished_good',
            notes: transfer.notes || '',
            items: [],
        });
        setShowEditModal(true);
        toast.error('Connection error. You can edit header info or add items.'); 
    }
};

    // Complete transfer
    const handleCompleteTransfer = async (transfer) => {
        const confirm = await MySwal.fire({
            title: 'Complete Transfer?',
            html: `<p>Transfer <strong>${transfer.transfer_no}</strong> will be completed.</p><p>Stock will be deducted from source.</p>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#28a745',
            confirmButtonText: 'Yes, Complete!',
        });
        if (!confirm.isConfirmed) return;

        try {
            const result = await manufacturingActions.updateTransferStatus(transfer.id, 'completed');
            if (result.success) {
                toast.success('Transfer completed. Stock updated.');
                setShowViewModal(false);
                loadTransfers(pagination.page, filters);
            } else {
                toast.error(result.message || 'Failed to complete transfer');
            }
        } catch (err) {
            toast.error(err.message || 'Failed to complete transfer');
        }
    };

    const handleCancelTransfer = async (transfer) => {
        const confirm = await MySwal.fire({
            title: 'Cancel Transfer?',
            html: `<p>Are you sure you want to cancel <strong>${transfer.transfer_no}</strong>?</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, Cancel',
        });
        if (!confirm.isConfirmed) return;

        try {
            const result = await manufacturingActions.updateTransferStatus(transfer.id, 'cancelled');
            if (result.success) {
                toast.success('Transfer cancelled');
                setShowViewModal(false);
                loadTransfers(pagination.page, filters);
            } else {
                toast.error(result.message || 'Failed to cancel transfer');
            }
        } catch (err) {
            toast.error(err.message || 'Failed to cancel transfer');
        }
    };

    // Badges
    const getStatusBadge = (status) => {
        const map = { 
            completed: ['bg-success', <CheckCircle size={11} key="completed-icon" />], 
            pending: ['bg-warning', <AlertCircle size={11} key="pending-icon" />], 
            cancelled: ['bg-danger', <XCircle size={11} key="cancelled-icon" />] 
        };
        const [cls, icon] = map[status] || ['bg-secondary', null];
        return <span className={`badge ${cls} d-inline-flex align-items-center gap-1`}>{icon}{(status || 'N/A').toUpperCase()}</span>;
    };
    
    const getTypeBadge = (type) => (
        <span className={`badge ${type === 'finished_good' ? 'bg-success' : 'bg-info'}`}>
            {type === 'finished_good' ? 'Finished Good' : 'Raw Material'}
        </span>
    );

    const productOptions = (Array.isArray(products) ? products : []).map(p => ({
        value: p.id,
        label: `${p.product_name} (${p.sku}) — Stock: ${p.quantity || 0} ${p.unit_name || ''}`,
    }));

    const totals = {
        totalTransfers: pagination.total,
        completed: transfers.filter(t => t.status === 'completed').length,
        totalQty: transfers.reduce((s, t) => s + parseFloat(t.total_quantity || 0), 0),
        totalValue: transfers.reduce((s, t) => s + parseFloat(t.total_value || 0), 0),
    };

    const formTotals = {
        qty: formData.items.reduce((s, i) => s + (parseFloat(i.quantity) || 0), 0),
        value: formData.items.reduce((s, i) => s + (parseFloat(i.total_cost) || 0), 0),
    };

    const renderTooltip = (props, text) => <Tooltip id={`tt-${text}`} {...props}>{text}</Tooltip>;

    const Pagination = () => {
        if (pagination.totalPages <= 1) return null;
        const { page, totalPages, total, limit } = pagination;
        return (
            <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="text-muted">
                    Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
                </div>
                <nav><ul className="pagination">
                    <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => loadTransfers(page - 1, filters)}>Previous</button>
                    </li>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const n = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                        return <li key={n} className={`page-item ${page === n ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => loadTransfers(n, filters)}>{n}</button>
                        </li>;
                    })}
                    <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => loadTransfers(page + 1, filters)}>Next</button>
                    </li>
                </ul></nav>
            </div>
        );
    };

    const renderHeaderFields = () => (
        <div className="row mb-4">
            <div className="col-md-3">
                <label className="form-label">Transfer Date *</label>
                <input type="date" name="transfer_date" className="form-control" value={formData.transfer_date}
                    onChange={e => setFormData(p => ({ ...p, transfer_date: e.target.value }))} />
            </div>
            <div className="col-md-3">
                <label className="form-label">Transfer Type *</label>
                <Select options={transferTypeOptions} value={transferTypeOptions.find(o => o.value === formData.transfer_type)}
                    onChange={sel => setFormData(p => ({ ...p, transfer_type: sel?.value || 'finished_good' }))} />
            </div>
            <div className="col-md-3">
                <label className="form-label">From Location *</label>
                <Select options={locationOptions} value={locationOptions.find(o => o.value === formData.from_location) || null}
                    onChange={sel => setFormData(p => ({ ...p, from_location: sel?.value || '' }))}
                    placeholder="Select source" className={errors.from_location ? 'is-invalid' : ''} />
                {errors.from_location && <div className="text-danger small mt-1">{errors.from_location}</div>}
            </div>
            <div className="col-md-3">
                <label className="form-label">To Location *</label>
                <Select options={locationOptions} value={locationOptions.find(o => o.value === formData.to_location) || null}
                    onChange={sel => setFormData(p => ({ ...p, to_location: sel?.value || '' }))}
                    placeholder="Select destination" className={errors.to_location ? 'is-invalid' : ''} />
                {errors.to_location && <div className="text-danger small mt-1">{errors.to_location}</div>}
            </div>
            <div className="col-md-12 mt-3">
                <label className="form-label">Notes</label>
                <textarea className="form-control" rows="2" value={formData.notes}
                    onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))}
                    placeholder="Additional notes" />
            </div>
        </div>
    );

    const renderItemsTable = () => (
        <div className="border-top pt-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-bold"><Package size={16} className="me-2" />Items to Transfer</h6>
                <button type="button" className="btn btn-sm btn-primary" onClick={addItem}>
                    <Plus size={14} className="me-1" />Add Product
                </button>
            </div>
            {errors.items && <div className="alert alert-danger">{errors.items}</div>}
            <div className="table-responsive">
                <table className="table table-bordered table-sm">
                    <thead className="table-light">
                        <tr>
                            <th style={{ width: '32%' }}>Product *</th>
                            <th style={{ width: '12%' }}>Qty *</th>
                            <th style={{ width: '9%' }}>Unit</th>
                            <th style={{ width: '16%' }}>Unit Cost (₹)</th>
                            <th style={{ width: '14%' }}>Total (₹)</th>
                            <th style={{ width: '15%' }}>Remarks</th>
                            <th style={{ width: '5%' }}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {formData.items.length === 0 ? (
                            <tr><td colSpan="7" className="text-center py-4 text-muted">Click Add Product to begin</td></tr>
                        ) : (
                            formData.items.map((item, idx) => (
                                <tr key={item.id || idx}>
                                    <td>
                                        <Select
                                            options={productOptions}
                                            value={productOptions.find(o => o.value === item.product_id) || null}
                                            onChange={sel => handleProductSelect(idx, sel)}
                                            placeholder="Select product..."
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                            className={errors[`product_${idx}`] ? 'is-invalid' : ''}
                                        />
                                        {errors[`product_${idx}`] && <div className="text-danger small mt-1">{errors[`product_${idx}`]}</div>}
                                        {errors[`stock_${idx}`] && <div className="text-danger small mt-1">{errors[`stock_${idx}`]}</div>}
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            className={`form-control form-control-sm ${errors[`qty_${idx}`] ? 'is-invalid' : ''}`}
                                            value={item.quantity || ''}
                                            onChange={e => handleQtyChange(idx, e.target.value)}
                                            min="0"
                                            step="0.01"
                                            placeholder="Qty"
                                        />
                                        {errors[`qty_${idx}`] && <div className="text-danger small mt-1">{errors[`qty_${idx}`]}</div>}
                                    </td>
                                    <td>
                                        <input type="text" className="form-control form-control-sm bg-light" value={item.unit} readOnly />
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            value={item.unit_cost || ''}
                                            onChange={e => handleCostChange(idx, e.target.value)}
                                            min="0"
                                            step="0.01"
                                            placeholder="Cost"
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm bg-light"
                                            value={parseFloat(item.total_cost || 0).toFixed(2)}
                                            readOnly
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            value={item.remarks || ''}
                                            placeholder="Remarks"
                                            onChange={e => {
                                                const val = e.target.value;
                                                setFormData(prev => {
                                                    const items = [...prev.items];
                                                    items[idx] = { ...items[idx], remarks: val };
                                                    return { ...prev, items };
                                                });
                                            }}
                                        />
                                    </td>
                                    <td className="text-center">
                                        <button type="button" className="btn btn-sm btn-danger" onClick={() => removeItem(idx)}>
                                            <Trash2 size={13} />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                    {formData.items.length > 0 && (
                        <tfoot className="table-light">
                            <tr>
                                <td colSpan="2" className="text-end fw-bold">Totals:</td>
                                <td className="fw-bold">{formTotals.qty.toFixed(2)} Units</td>
                                <td></td>
                                <td className="fw-bold">{formatCurrency(formTotals.value)}</td>
                                <td colSpan="2"></td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        </div>
    );

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="page-title"><h4>Stock Transfer</h4><h6>Transfer stock between locations</h6></div>
                    <ul className="table-top-head">
                        {[['PDF', 'pdf'], ['Excel', 'excel']].map(([l, i]) => (
                            <li key={l}>
                                <OverlayTrigger placement="top" overlay={p => renderTooltip(p, l)}>
                                    <Link to="#"><ImageWithBasePath src={`assets/img/icons/${i}.svg`} alt={l} /></Link>
                                </OverlayTrigger>
                            </li>
                        ))}
                        <li>
                            <OverlayTrigger placement="top" overlay={p => renderTooltip(p, 'Print')}>
                                <Link onClick={() => window.print()}><Printer /></Link>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={p => renderTooltip(p, 'Refresh')}>
                                <Link onClick={() => loadTransfers(1, filters)}><RotateCcw /></Link>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={p => renderTooltip(p, 'Collapse')}>
                                <Link className={reduxData ? 'active' : ''} onClick={() => dispatch(setToogleHeader(!reduxData))}>
                                    <ChevronUp />
                                </Link>
                            </OverlayTrigger>
                        </li>
                    </ul>
                    {canCreate && (
                        <div className="page-btn">
                            <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
                                <Plus size={18} className="me-2" />New Transfer
                            </button>
                        </div>
                    )}
                </div>

                {/* Stat cards */}
                <div className="row">
                    {[
                        { label: 'Total Transfers', value: totals.totalTransfers, icon: <Shuffle size={24} className="text-primary" />, bg: 'bg-primary' },
                        { label: 'Completed', value: totals.completed, icon: <CheckCircle size={24} className="text-success" />, bg: 'bg-success' },
                        { label: 'Total Qty', value: totals.totalQty.toLocaleString('en-IN'), icon: <Package size={24} className="text-warning" />, bg: 'bg-warning' },
                        { label: 'Total Value', value: formatCurrency(totals.totalValue), icon: <DollarSign size={24} className="text-info" />, bg: 'bg-info' },
                    ].map(({ label, value, icon, bg }) => (
                        <div key={label} className="col-lg-3 col-md-6">
                            <div className="card"><div className="card-body">
                                <div className="d-flex align-items-center">
                                    <div className={`${bg} bg-opacity-10 p-3 rounded me-3`}>{icon}</div>
                                    <div><p className="text-muted mb-1">{label}</p><h3 className="mb-0">{value}</h3></div>
                                </div>
                            </div></div>
                        </div>
                    ))}
                </div>

                {/* Filters */}
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="row">
                            <div className="col-lg-3">
                                <label>Search</label>
                                <div className="input-group">
                                    <input type="text" className="form-control" placeholder="Transfer no..."
                                        value={filters.search} onChange={e => handleFilterChange('search', e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && loadTransfers(1, filters)} />
                                    <button className="btn btn-primary" onClick={() => loadTransfers(1, filters)}>
                                        <Search size={16} />
                                    </button>
                                </div>
                            </div>
                            <div className="col-lg-2">
                                <label>Type</label>
                                <Select options={transferTypeOptions} isClearable placeholder="All Types"
                                    value={transferTypeOptions.find(o => o.value === filters.transfer_type) || null}
                                    onChange={sel => handleFilterChange('transfer_type', sel?.value || '')} />
                            </div>
                            <div className="col-lg-2">
                                <label>Status</label>
                                <Select options={statusOptions} isClearable placeholder="All Status"
                                    value={statusOptions.find(o => o.value === filters.status) || null}
                                    onChange={sel => handleFilterChange('status', sel?.value || '')} />
                            </div>
                            <div className="col-lg-2">
                                <label>From</label>
                                <Select options={locationOptions} isClearable placeholder="All Locations"
                                    value={locationOptions.find(o => o.value === filters.from_location) || null}
                                    onChange={sel => handleFilterChange('from_location', sel?.value || '')} />
                            </div>
                            <div className="col-lg-2">
                                <label>To</label>
                                <Select options={locationOptions} isClearable placeholder="All Locations"
                                    value={locationOptions.find(o => o.value === filters.to_location) || null}
                                    onChange={sel => handleFilterChange('to_location', sel?.value || '')} />
                            </div>
                            <div className="col-lg-1">
                                <label>&nbsp;</label>
                                <button className="btn btn-secondary w-100" onClick={clearFilters}>Clear</button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="card">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-5"><div className="spinner-border text-primary" role="status" /></div>
                        ) : transfers.length === 0 ? (
                            <div className="text-center py-5">
                                <Shuffle size={48} className="text-muted mb-3" />
                                <h5>No transfers found</h5>
                                {canCreate && (
                                    <button className="btn btn-primary mt-2" onClick={() => setShowCreateModal(true)}>
                                        <Plus size={16} className="me-2" />New Transfer
                                    </button>
                                )}
                            </div>
                        ) : (
                            <>
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead>
                                            <tr>
                                                <th>Transfer No</th>
                                                <th>Date</th>
                                                <th>From</th>
                                                <th>To</th>
                                                <th>Type</th>
                                                <th>Items</th>
                                                <th>Qty</th>
                                                <th>Value</th>
                                                <th>Status</th>
                                                <th className="text-center">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {transfers.map(t => (
                                                <tr key={t.id}>
                                                    <td><strong>{t.transfer_no}</strong></td>
                                                    <td><Calendar size={13} className="me-1 text-muted" />{formatDate(t.transfer_date)}</td>
                                                    <td><MapPin size={13} className="me-1 text-muted" />{t.from_location}</td>
                                                    <td><MapPin size={13} className="me-1 text-muted" />{t.to_location}</td>
                                                    <td>{getTypeBadge(t.transfer_type)}</td>
                                                    <td>{t.total_items || 0}</td>
                                                    <td>{parseFloat(t.total_quantity || 0).toLocaleString('en-IN')}</td>
                                                    <td className="fw-bold">{formatCurrency(t.total_value)}</td>
                                                    <td>{getStatusBadge(t.status)}</td>
                                                    <td className="text-center">
                                                        <button className="btn btn-sm btn-outline-info me-1" onClick={() => handleViewTransfer(t)} title="View">
                                                            <Eye size={14} />
                                                        </button>
                                                        {canEdit && t.status === 'pending' && (
                                                            <>
                                                                <button className="btn btn-sm btn-outline-primary me-1" onClick={() => handleEditTransfer(t)} title="Edit">
                                                                    <Edit size={14} />
                                                                </button>
                                                                <button className="btn btn-sm btn-outline-success me-1" onClick={() => handleCompleteTransfer(t)} title="Complete">
                                                                    <CheckCircle size={14} />
                                                                </button>
                                                                <button className="btn btn-sm btn-outline-danger" onClick={() => handleCancelTransfer(t)} title="Cancel">
                                                                    <XCircle size={14} />
                                                                </button>
                                                            </>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <Pagination />
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Create Modal */}
            <Modal show={showCreateModal} onHide={() => { setShowCreateModal(false); setFormData(emptyForm); setErrors({}); }} size="xl" centered scrollable>
                <Modal.Header closeButton>
                    <Modal.Title><Shuffle size={18} className="me-2" />New Stock Transfer</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {renderHeaderFields()}
                    {renderItemsTable()}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => { setShowCreateModal(false); setFormData(emptyForm); setErrors({}); }}>Cancel</Button>
                    <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
                        {submitting ? <><span className="spinner-border spinner-border-sm me-2" />Creating...</> : <><Shuffle size={16} className="me-2" />Create Transfer</>}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* View Modal */}
            <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg" centered scrollable>
                <Modal.Header closeButton>
                    <Modal.Title><Eye size={18} className="me-2" />Transfer Details — {selectedTransfer?.transfer_no}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedTransfer && (
                        <>
                            <div className="row mb-4">
                                <div className="col-md-6">
                                    <div className="card bg-light">
                                        <div className="card-body">
                                            <h6>Transfer Info</h6>
                                            <p><strong>No:</strong> {selectedTransfer.transfer_no}</p>
                                            <p><strong>Date:</strong> {formatDate(selectedTransfer.transfer_date)}</p>
                                            <p><strong>From:</strong> {selectedTransfer.from_location}</p>
                                            <p><strong>To:</strong> {selectedTransfer.to_location}</p>
                                            <p><strong>Type:</strong> {getTypeBadge(selectedTransfer.transfer_type)}</p>
                                            <p><strong>Status:</strong> {getStatusBadge(selectedTransfer.status)}</p>
                                            {selectedTransfer.notes && <p><strong>Notes:</strong> {selectedTransfer.notes}</p>}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="card bg-light">
                                        <div className="card-body">
                                            <h6>Summary</h6>
                                            <p><strong>Items:</strong> {selectedTransfer.total_items || selectedTransfer.items?.length || 0}</p>
                                            <p><strong>Qty:</strong> {parseFloat(selectedTransfer.total_quantity || 0).toLocaleString('en-IN')}</p>
                                            <p><strong>Value:</strong> {formatCurrency(selectedTransfer.total_value)}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {selectedTransfer.items?.length > 0 && (
                                <div className="table-responsive">
                                    <table className="table table-bordered table-sm">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Product</th>
                                                <th>SKU</th>
                                                <th>Qty</th>
                                                <th>Unit</th>
                                                <th>Unit Cost</th>
                                                <th>Total</th>
                                                <th>Remarks</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedTransfer.items.map((item, i) => (
                                                <tr key={i}>
                                                    <td>{item.product_name}</td>
                                                    <td><code>{item.sku}</code></td>
                                                    <td className="fw-bold">{parseFloat(item.quantity).toLocaleString('en-IN')}</td>
                                                    <td>{item.unit}</td>
                                                    <td>{formatCurrency(item.unit_cost)}</td>
                                                    <td className="fw-bold">{formatCurrency(item.total_cost)}</td>
                                                    <td>{item.remarks || '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            {selectedTransfer.status === 'pending' && (
                                <div className="alert alert-info mt-3">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span><AlertCircle size={16} className="me-2" />Pending — complete to update stock levels</span>
                                        <div>
                                            <button className="btn btn-sm btn-success me-2" onClick={() => handleCompleteTransfer(selectedTransfer)}>
                                                <CheckCircle size={14} className="me-1" />Complete
                                            </button>
                                            <button className="btn btn-sm btn-danger" onClick={() => handleCancelTransfer(selectedTransfer)}>
                                                <XCircle size={14} className="me-1" />Cancel
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {selectedTransfer.status === 'completed' && (
                                <div className="alert alert-success mt-3">
                                    <CheckCircle size={16} className="me-2" />Transfer completed. Stock levels updated.
                                </div>
                            )}
                            {selectedTransfer.status === 'cancelled' && (
                                <div className="alert alert-danger mt-3">
                                    <XCircle size={16} className="me-2" />Transfer cancelled. No stock changes made.
                                </div>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {selectedTransfer?.status === 'pending' && canEdit && (
                        <Button variant="primary" onClick={() => { setShowViewModal(false); handleEditTransfer(selectedTransfer); }}>
                            <Edit size={16} className="me-2" />Edit
                        </Button>
                    )}
                    <Button variant="secondary" onClick={() => setShowViewModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>

            {/* Edit Modal */}
            <Modal show={showEditModal} onHide={() => { setShowEditModal(false); setFormData(emptyForm); setErrors({}); setSelectedTransfer(null); }} size="xl" centered scrollable>
                <Modal.Header closeButton>
                    <Modal.Title><Edit size={18} className="me-2" />Edit Transfer — {selectedTransfer?.transfer_no}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {renderHeaderFields()}
                    {renderItemsTable()}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => { setShowEditModal(false); setFormData(emptyForm); setErrors({}); setSelectedTransfer(null); }}>Cancel</Button>
                    <Button variant="primary" onClick={handleUpdateTransfer} disabled={submitting}>
                        {submitting ? <><span className="spinner-border spinner-border-sm me-2" />Updating...</> : <><CheckCircle size={16} className="me-2" />Update Transfer</>}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default StockTransfer;